import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm, SubmitHandler } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAuth } from '@/hooks/useAuth'
import { missionsApi, CreateMission } from '@/api/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Link, useNavigate } from 'react-router-dom'
import { useToast } from '@/hooks/useToast'
import {
  Target,
  Plus,
  Search,
  Filter,
  ArrowRight,
  Calendar,
  User,
  Edit2,
  Trash2,
  Loader2,
} from 'lucide-react'

/**
 * Mission creation form schema - platform-level core fields only.
 * WHY: Missions are cross-module entities. SoftwareIQ-specific fields
 * (CIA categorization, analysts, etc.) are captured in the Profile workflow step.
 */
const missionFormSchema = z.object({
  mission_name: z.string().min(1, 'Mission name is required').max(255),
  mission_objective: z.string().max(2048).optional(),
  status: z.enum(['active', 'completed', 'archived']),
  classification: z.string().max(50).optional(),
})

type MissionFormData = z.infer<typeof missionFormSchema>

export function MissionsPage() {
  // Note: useAuth is kept for future authenticated API calls
  useAuth()
  const { toast } = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = useState('')
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [editingMission, setEditingMission] = useState<{
    id: string
    name: string
    objective?: string
    status: string
    classification?: string
  } | null>(null)
  const [deletingMissionId, setDeletingMissionId] = useState<string | null>(null)

  // Form for creating missions - core platform fields only
  const createForm = useForm<MissionFormData>({
    resolver: zodResolver(missionFormSchema),
    defaultValues: {
      mission_name: '',
      mission_objective: '',
      status: 'active',
      classification: '',
    },
  })

  // Form for editing missions - core platform fields only
  const editForm = useForm<MissionFormData>({
    resolver: zodResolver(missionFormSchema),
    defaultValues: {
      mission_name: '',
      mission_objective: '',
      status: 'active',
      classification: '',
    },
  })

  const { data: missions = [], isLoading } = useQuery({
    queryKey: ['missions'],
    queryFn: () => missionsApi.list(),
  })

  const createMutation = useMutation({
    mutationFn: (data: CreateMission) => missionsApi.create(data),
    onSuccess: (newMission) => {
      queryClient.invalidateQueries({ queryKey: ['missions'] })
      setIsCreateDialogOpen(false)
      createForm.reset()
      toast({
        title: 'Mission Created',
        description: 'Redirecting to workflow to complete mission setup...',
      })
      // Navigate to the Software Assurance workflow to capture module-specific details
      navigate(`/software-assurance?mission=${newMission.mission_id}`)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create mission',
        variant: 'destructive',
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: MissionFormData }) =>
      missionsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['missions'] })
      setIsEditDialogOpen(false)
      setEditingMission(null)
      editForm.reset()
      toast({
        title: 'Mission Updated',
        description: 'Mission has been updated successfully.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update mission',
        variant: 'destructive',
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => missionsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['missions'] })
      setDeletingMissionId(null)
      toast({
        title: 'Mission Deleted',
        description: 'Mission has been deleted successfully.',
      })
    },
    onError: (error) => {
      setDeletingMissionId(null)
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete mission',
        variant: 'destructive',
      })
    },
  })

  const filteredMissions = missions.filter(
    (m) =>
      m.mission_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.mission_objective?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleCreateSubmit: SubmitHandler<MissionFormData> = (data) => {
    createMutation.mutate({
      mission_name: data.mission_name,
      mission_objective: data.mission_objective || undefined,
      status: data.status,
      classification: data.classification || undefined,
    })
  }

  const handleEditSubmit: SubmitHandler<MissionFormData> = (data) => {
    if (!editingMission) return
    updateMutation.mutate({
      id: editingMission.id,
      data,
    })
  }

  const openEditDialog = (mission: {
    mission_id: string
    mission_name: string
    mission_objective?: string
    status: string
    classification?: string
  }) => {
    setEditingMission({
      id: mission.mission_id,
      name: mission.mission_name,
      objective: mission.mission_objective,
      status: mission.status,
      classification: mission.classification,
    })
    editForm.reset({
      mission_name: mission.mission_name,
      mission_objective: mission.mission_objective || '',
      status: mission.status as 'active' | 'completed' | 'archived',
      classification: mission.classification || '',
    })
    setIsEditDialogOpen(true)
  }

  const handleDelete = (missionId: string) => {
    if (window.confirm('Are you sure you want to delete this mission? This action cannot be undone.')) {
      setDeletingMissionId(missionId)
      deleteMutation.mutate(missionId)
    }
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display font-bold">Missions</h2>
          <p className="text-muted-foreground">
            Manage your software assurance missions and test objectives
          </p>
        </div>

        {/* Create Mission Dialog - Platform-level core fields only */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="glow">
              <Plus className="h-4 w-4 mr-2" />
              New Mission
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <form onSubmit={createForm.handleSubmit(handleCreateSubmit)}>
              <DialogHeader>
                <DialogTitle>Create New Mission</DialogTitle>
                <DialogDescription>
                  Create a new mission. After creation, you'll configure module-specific
                  details in the workflow.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="mission_name">
                    Mission Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="mission_name"
                    placeholder="e.g., OFRAK v3.2.0 Security Assessment"
                    {...createForm.register('mission_name')}
                    aria-invalid={!!createForm.formState.errors.mission_name}
                    aria-describedby="mission_name_error"
                  />
                  {createForm.formState.errors.mission_name && (
                    <p id="mission_name_error" className="text-sm text-destructive">
                      {createForm.formState.errors.mission_name.message}
                    </p>
                  )}
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="mission_objective">Objective</Label>
                  <Textarea
                    id="mission_objective"
                    placeholder="Describe the mission objectives and scope..."
                    rows={3}
                    {...createForm.register('mission_objective')}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="status">Status</Label>
                    <Select
                      defaultValue="active"
                      onValueChange={(value) =>
                        createForm.setValue('status', value as 'active' | 'completed' | 'archived')
                      }
                    >
                      <SelectTrigger id="status">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="archived">Archived</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="classification">Classification</Label>
                    <Select
                      onValueChange={(value) => createForm.setValue('classification', value)}
                    >
                      <SelectTrigger id="classification">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="UNCLASSIFIED">Unclassified</SelectItem>
                        <SelectItem value="CUI">CUI</SelectItem>
                        <SelectItem value="SECRET">Secret</SelectItem>
                        <SelectItem value="TOP SECRET">Top Secret</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsCreateDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Create Mission
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit Mission Dialog - Platform-level core fields only */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <form onSubmit={editForm.handleSubmit(handleEditSubmit)}>
            <DialogHeader>
              <DialogTitle>Edit Mission</DialogTitle>
              <DialogDescription>
                Update the mission details. Module-specific settings can be edited
                in the workflow.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="edit_mission_name">
                  Mission Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="edit_mission_name"
                  {...editForm.register('mission_name')}
                  aria-invalid={!!editForm.formState.errors.mission_name}
                />
                {editForm.formState.errors.mission_name && (
                  <p className="text-sm text-destructive">
                    {editForm.formState.errors.mission_name.message}
                  </p>
                )}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="edit_mission_objective">Objective</Label>
                <Textarea
                  id="edit_mission_objective"
                  rows={3}
                  {...editForm.register('mission_objective')}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="edit_status">Status</Label>
                  <Select
                    value={editForm.watch('status')}
                    onValueChange={(value) =>
                      editForm.setValue('status', value as 'active' | 'completed' | 'archived')
                    }
                  >
                    <SelectTrigger id="edit_status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="edit_classification">Classification</Label>
                  <Select
                    value={editForm.watch('classification') || ''}
                    onValueChange={(value) => editForm.setValue('classification', value)}
                  >
                    <SelectTrigger id="edit_classification">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UNCLASSIFIED">Unclassified</SelectItem>
                      <SelectItem value="CUI">CUI</SelectItem>
                      <SelectItem value="SECRET">Secret</SelectItem>
                      <SelectItem value="TOP SECRET">Top Secret</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Search and filters */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search missions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <Button variant="outline">
          <Filter className="h-4 w-4 mr-2" />
          Filters
        </Button>
      </div>

      {/* Missions grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-fade-in">
          {filteredMissions.map((mission) => (
            <Card key={mission.mission_id} className="group hover:border-primary/50 transition-colors">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Target className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={mission.status === 'active' ? 'default' : 'secondary'}>
                      {mission.status}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => openEditDialog(mission)}
                      aria-label={`Edit ${mission.mission_name}`}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive"
                      onClick={() => handleDelete(mission.mission_id)}
                      disabled={deletingMissionId === mission.mission_id}
                      aria-label={`Delete ${mission.mission_name}`}
                    >
                      {deletingMissionId === mission.mission_id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
                <CardTitle className="text-lg mt-3 group-hover:text-primary transition-colors">
                  {mission.mission_name}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {mission.mission_objective || 'No objective specified'}
                </p>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {new Date(mission.created_at).toLocaleDateString()}
                  </div>
                  {mission.created_by && (
                    <div className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {mission.created_by}
                    </div>
                  )}
                </div>
                <Button asChild variant="ghost" className="w-full justify-between">
                  <Link to={`/missions/${mission.mission_id}`}>
                    View Details
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ))}

          {filteredMissions.length === 0 && (
            <div className="col-span-full text-center py-12">
              <Target className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium">No missions found</h3>
              <p className="text-muted-foreground mt-1">
                {searchQuery
                  ? 'Try adjusting your search query'
                  : 'Create your first mission to get started'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
